import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.neoncrowgames.com';

export { API_BASE_URL };

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const { token } = useAuthStore.getState();
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  // Auth endpoints
  async login(username: string, password: string) {
    return this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }

  async register(username: string, email: string, password: string) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, email, password }),
    });
  }

  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  // Deck endpoints
  async getDecks() {
    return this.request('/api/decks');
  }

  async getPublicDecks() {
    return this.request('/api/decks/public');
  }

  async createDeck(deck: any) {
    return this.request('/api/decks', {
      method: 'POST',
      body: JSON.stringify(deck),
    });
  }

  async getDeck(id: number) {
    return this.request(`/api/decks/${id}`);
  }

  async updateDeck(id: number, deck: any) {
    return this.request(`/api/decks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(deck),
    });
  }

  async deleteDeck(id: number) {
    return this.request(`/api/decks/${id}`, {
      method: 'DELETE',
    });
  }

  // Match endpoints
  async getMatches() {
    return this.request('/api/matches');
  }

  async createMatch(match: any) {
    return this.request('/api/matches', {
      method: 'POST',
      body: JSON.stringify(match),
    });
  }

  async getMatch(id: number) {
    return this.request(`/api/matches/${id}`);
  }

  async joinMatch(id: number) {
    return this.request(`/api/matches/${id}/join`, {
      method: 'POST',
    });
  }

  async leaveMatch(id: number) {
    return this.request(`/api/matches/${id}/leave`, {
      method: 'POST',
    });
  }

  async startMatch(id: number) {
    return this.request(`/api/matches/${id}/start`, {
      method: 'POST',
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
